import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, MessageCircleDashed, PanelLeftClose, Search } from 'lucide-react';
import { cn } from '@/shared/cn';
import { GLASS_INPUT, HEADER_ICON_BUTTON } from '@/shared/ui-class';
import { Commentable } from '@/shared/comments/Commentable';
import { SIDEBAR_ROW_ACTIVE } from '@/versions/v1/chrome/sidebar/SidebarShell';
import { useChat } from '@/versions/v1/shared/ChatContext';

export function RailHistoryOverlay({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { threads, currentThreadId, selectThread } = useChat();
  const [query, setQuery] = useState('');
  const [entered, setEntered] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const trimmedQuery = query.trim().toLowerCase();
  const visibleThreads = useMemo(() => {
    if (!trimmedQuery) return threads;
    return threads.filter((t) => t.title.toLowerCase().includes(trimmedQuery));
  }, [threads, trimmedQuery]);

  const pinned = visibleThreads.filter((t) => t.pinned);
  const recents = visibleThreads.filter((t) => !t.pinned);

  const onSelect = useCallback(
    (id: string) => {
      selectThread(id);
      onClose();
    },
    [selectThread, onClose],
  );

  if (!open) return null;

  return (
    <Commentable name="History">
      <div className="absolute inset-0 z-20">
        <div
          data-testid="rail-history-backdrop"
          className="absolute inset-0 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 z-30 flex w-[85%] max-w-[320px] flex-col border-r border-divider bg-[var(--background)] transition-transform duration-200 ease-out motion-reduce:transition-none',
            entered ? 'translate-x-0' : '-translate-x-full',
          )}
        >
        <header className="flex h-12 flex-shrink-0 items-center justify-between gap-2 border-b border-divider px-3">
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Chat history
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close history"
            title="Close history"
            className={HEADER_ICON_BUTTON}
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-shrink-0 px-2.5 pb-2 pt-2">
          <div className="relative h-8">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400"
              aria-hidden
            />
            <input
              ref={searchInputRef}
              type="search"
              aria-label="Search chat history"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chat history…"
              className={cn(
                'h-8 w-full rounded-md border pl-[30px] pr-3 text-sm font-normal text-zinc-900 outline-none transition-colors placeholder:font-light placeholder:text-zinc-400 focus:border-[var(--glass-border-strong)] dark:text-zinc-100 dark:placeholder:text-zinc-500',
                GLASS_INPUT,
              )}
            />
          </div>
        </div>

        <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {trimmedQuery && visibleThreads.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted">No matches</p>
          ) : (
            <>
              <ThreadSection title="Pinned" threads={pinned} currentId={currentThreadId} onSelect={onSelect} />
              <ThreadSection title="Recents" threads={recents} currentId={currentThreadId} onSelect={onSelect} />
            </>
          )}
        </div>
        </div>
      </div>
    </Commentable>
  );
}

function ThreadSection({
  title,
  threads,
  currentId,
  onSelect,
}: {
  title: string;
  threads: { id: string; title: string }[];
  currentId: string;
  onSelect: (id: string) => void;
}) {
  if (threads.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
        {title}
      </div>
      <ul>
        {threads.map((t) => {
          const active = t.id === currentId;
          const Icon = active ? MessageCircle : MessageCircleDashed;
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                  active ? SIDEBAR_ROW_ACTIVE : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900',
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0 text-zinc-400" strokeWidth={1.6} />
                <span className="truncate">{t.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
